import { useTranslation } from 'react-i18next';
import { usePaymentForm } from '../hooks/usePaymentForm';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';

export default function PaymentForm({ person }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        paymentFormRegister,
        paymentErrors,
        isActiveChecked,
        setPaymentValue,
        paymentData,
        handleEdit,
        handleDelete,
        isEditing,
        handleSubmit,
        submitPaymentForm,
        handleCancel,
        loading,
        watch,
        banks,
    } = usePaymentForm(person);

    const handleBankSelect = (e) => {
        const selectedBank = banks.find(b => String(b.branchId) === e.target.value);
        if (selectedBank) {
            setPaymentValue('bankId', selectedBank.branchId);
            setPaymentValue('bic', selectedBank.bankCode);
            setPaymentValue('bankName', selectedBank.name);
        } else {
            setPaymentValue('bankId', '');
            setPaymentValue('bic', '');
            setPaymentValue('bankName', '');
        }
    };

    const onPaymentSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(submitPaymentForm)(e);
    };

    return (
        <>
            <div className="payment-form-container">
                <h3 className="form-subtitle">
                    {t('persons.payment.title', {
                        name: [person.name, person.fathersName, person.surname]
                            .filter(Boolean)
                            .join(' ')
                    })}
                </h3>

                <div className="form-content">
                    <div className="form-section">
                        <form onSubmit={onPaymentSubmit}>
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.payment.chooseBank')} <span className="required">*</span></span>
                                <div className='form-input-fields'>
                                    <select
                                        name="bankId"
                                        className={`form-control ${paymentErrors.bic ? 'is-invalid' : ''}`}
                                        {...paymentFormRegister('bankId')}
                                        onChange={handleBankSelect}
                                        disabled={loading}
                                        value={watch('bankId') || ''}
                                    >
                                        <option value="">{loading ? 'Loading...' : t('persons.banks.selectBank')}</option>
                                        {banks.map(bank => (
                                            <option key={bank.branchId} value={bank.branchId}>
                                                {bank.name} ({bank.bankCode})
                                            </option>
                                        ))}
                                    </select>
                                    {paymentErrors.bic && (
                                        <div className="invalid-feedback">{paymentErrors.bic.message}</div>
                                    )}
                                    <button type="button" className="form-btn btn-green" onClick={() => navigate(`/payments/settings/banks`)}>
                                        {t('common.viewBanks')}
                                    </button>
                                </div>
                            </div>
                            <div className="form-input-wrapper flex-row">
                                <div className='form-input-fields width-50'>
                                    <span className="span-bold">BIC <span className="required">*</span></span>
                                    <input
                                        type="text"
                                        name="bic"
                                        placeholder="BIC"
                                        className="form-control"
                                        {...paymentFormRegister('bic')}
                                        readOnly
                                    />
                                </div>
                                <div className='form-input-fields width-50'>
                                    <span className="span-bold">{t('persons.payment.bankName')} <span className="required">*</span></span>
                                    <input
                                        type="text"
                                        name="bankName"
                                        placeholder={t('persons.payment.bankName')}
                                        className="form-control"
                                        {...paymentFormRegister('bankName')}
                                        readOnly
                                    />
                                    {paymentErrors.bankName && (
                                        <div className="invalid-feedback">{paymentErrors.bankName.message}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-input-wrapper">
                                <span className="span-bold">IBAN <span className="required">*</span></span>
                                <div className='form-input-fields'>
                                    <input
                                        type="text"
                                        name="iban"
                                        placeholder="IBAN"
                                        className="form-control"
                                        {...paymentFormRegister('iban')}
                                    />
                                    {paymentErrors.iban && (
                                        <div className="invalid-feedback">{paymentErrors.iban.message}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.payment.note')}</span>
                                <div className='form-input-fields'>
                                    <input
                                        type="text"
                                        name="description"
                                        placeholder={t('persons.payment.note')}
                                        className={`form-control ${paymentErrors.description ? 'is-invalid' : ''}`}
                                        {...paymentFormRegister('description')}
                                    />
                                    {paymentErrors.description && (
                                        <div className="invalid-feedback">{paymentErrors.description.message}</div>
                                    )}
                                </div>
                            </div>

                            <div className="payment-form-section">
                                <div className="checkbox-group">
                                    <div className="input-checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            {...paymentFormRegister('isActive')}
                                            checked={isActiveChecked}
                                            onChange={(e) => setPaymentValue('isActive', e.target.checked)}
                                        />
                                        <label>{t('persons.payment.active')}</label>
                                    </div>
                                </div>

                                <div className="payment-form-controls">
                                    <button
                                        type="button"
                                        className={`form-btn ${isEditing ? 'btn-orange' : 'btn-green'}`}
                                        onClick={handleSubmit(submitPaymentForm)}
                                    >
                                        {isEditing ? t('common.edit') : t('common.add')}
                                    </button>
                                    {isEditing && (
                                        <button
                                            type="button"
                                            className="form-btn btn-gray"
                                            onClick={handleCancel}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="table-section">
                        <div className="payment-list-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>{t('persons.payment.status')}</th>
                                        <th>{t('persons.payment.bic')}</th>
                                        <th>{t('persons.payment.iban')}</th>
                                        <th>{t('persons.payment.bankName')}</th>
                                        <th>{t('persons.payment.note')}</th>
                                        <th>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentData?.map((payment, index) => (
                                        <tr key={index}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={payment.isActive}
                                                    disabled
                                                />
                                            </td>
                                            <td>{payment.bank.bankCode}</td>
                                            <td>{payment.iban}</td>
                                            <td>{payment.bank.name}</td>
                                            <td>{payment.description}</td>
                                            <td className="action-buttons">
                                                <FontAwesomeIcon
                                                    icon={faEdit}
                                                    className="action-icon edit"
                                                    onClick={() => handleEdit(payment)}
                                                />
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    className="action-icon delete"
                                                    onClick={() => handleDelete(payment.id)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
} 